import * as React from 'react';
import {
  isAuthenticated as checkAuth,
  login as authLogin,
  logout as authLogout,
  silentRefresh,
  exchangeCodeForTokens,
  getAccessToken,
  hasRefreshToken,
  checkSessionTimeout,
  updateLastActivity,
} from '../services/auth-client';

/**
 * Authentication state management using React Context + useReducer.
 * Adapted from the Cognito pattern to use EntraID OAuth2 Authorization Code flow.
 *
 * 30-minute session timeout is enforced via activity tracking.
 * Tokens are stored in memory only (never localStorage).
 */

// ─── State ───────────────────────────────────────────────────────────────────

export interface AuthState {
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
  readonly error: string | null;
}

export const initialAuthState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// ─── Actions ─────────────────────────────────────────────────────────────────

type AuthAction =
  | { readonly type: 'AUTH_LOADING' }
  | { readonly type: 'AUTH_SUCCESS' }
  | { readonly type: 'AUTH_FAILURE'; readonly payload: string }
  | { readonly type: 'AUTH_LOGOUT' }
  | { readonly type: 'AUTH_SESSION_EXPIRED' };

// ─── Reducer ─────────────────────────────────────────────────────────────────

export function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_LOADING':
      return { ...state, isLoading: true, error: null };
    case 'AUTH_SUCCESS':
      return { isAuthenticated: true, isLoading: false, error: null };
    case 'AUTH_FAILURE':
      return { isAuthenticated: false, isLoading: false, error: action.payload };
    case 'AUTH_LOGOUT':
      return { isAuthenticated: false, isLoading: false, error: null };
    case 'AUTH_SESSION_EXPIRED':
      return { isAuthenticated: false, isLoading: false, error: 'Session expired. Please log in again.' };
    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface AuthContextValue {
  readonly state: AuthState;
  readonly login: () => void;
  readonly logout: () => void;
  readonly handleCallback: (code: string) => Promise<void>;
  readonly getAccessToken: () => string | null;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

interface AuthProviderProps {
  readonly children: React.ReactNode;
}

/**
 * AuthProvider wraps the application and manages EntraID OAuth2 authentication state.
 * On mount, attempts silent refresh if a refresh token exists in sessionStorage.
 * Enforces 30-minute session timeout on user activity.
 */
export function AuthProvider({ children }: AuthProviderProps): React.JSX.Element {
  const [state, dispatch] = React.useReducer(authReducer, initialAuthState);
  const sessionCheckRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  // Attempt session restoration on mount
  React.useEffect(() => {
    async function restoreSession(): Promise<void> {
      if (!hasRefreshToken()) {
        dispatch({ type: 'AUTH_FAILURE', payload: '' });
        return;
      }

      const refreshed = await silentRefresh();
      if (refreshed) {
        dispatch({ type: 'AUTH_SUCCESS' });
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: '' });
      }
    }

    restoreSession();
  }, []);

  // Session timeout checker — runs every 60 seconds
  React.useEffect(() => {
    if (!state.isAuthenticated) {
      if (sessionCheckRef.current) {
        clearInterval(sessionCheckRef.current);
        sessionCheckRef.current = null;
      }
      return;
    }

    sessionCheckRef.current = setInterval(() => {
      if (checkSessionTimeout()) {
        dispatch({ type: 'AUTH_SESSION_EXPIRED' });
        authLogin();
      }
    }, 60_000);

    return () => {
      if (sessionCheckRef.current) {
        clearInterval(sessionCheckRef.current);
      }
    };
  }, [state.isAuthenticated]);

  // Track user activity for session timeout
  React.useEffect(() => {
    if (!state.isAuthenticated) return;

    const handleActivity = (): void => {
      updateLastActivity();
    };

    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('keydown', handleActivity);

    return () => {
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('keydown', handleActivity);
    };
  }, [state.isAuthenticated]);

  const login = React.useCallback((): void => {
    authLogin();
  }, []);

  const logout = React.useCallback((): void => {
    dispatch({ type: 'AUTH_LOGOUT' });
    authLogout();
  }, []);

  const handleCallback = React.useCallback(async (code: string): Promise<void> => {
    dispatch({ type: 'AUTH_LOADING' });
    try {
      await exchangeCodeForTokens(code);
      dispatch({ type: 'AUTH_SUCCESS' });
    } catch (err) {
      dispatch({
        type: 'AUTH_FAILURE',
        payload: err instanceof Error ? err.message : 'Authentication failed',
      });
    }
  }, []);

  const contextValue = React.useMemo(
    (): AuthContextValue => ({
      state,
      login,
      logout,
      handleCallback,
      getAccessToken,
    }),
    [state, login, logout, handleCallback],
  );

  return React.createElement(AuthContext.Provider, { value: contextValue }, children);
}

// ─── Consumer Hook ───────────────────────────────────────────────────────────

/**
 * Hook to access authentication state and actions.
 * Must be used within an AuthProvider.
 */
export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
